// The agent LOOP and the money that gates it: one session's boot, model calls,
// tool calls (native and MCP), the policy gate, the escalation and handoff it
// fires, the mail leg of that escalation, and the account pause/resume plus BYOK
// key + billing-preference lifecycle those decisions turn on.
// Split out of `../audit.ts`, which is now the barrel over this directory. Not in
// the barrel: this is a HALF of `AuditEventKind`, exported only so `./kind` can
// assemble it. Import `AuditEventKind` — never a group.

export const AUDIT_KIND_LOOP = {
  SessionBoot: 'session.boot',
  ModelCall: 'model.call',
  ModelReasoning: 'model.reasoning',
  // Plan 101 slice 02: one row per in-cluster session STEP (`packages/agent`
  // `loop/model-step.ts`) — the model id the provider answered with, input /
  // output / cache-read tokens and the finish reason. `model.call` stays the
  // classifier's row; this is the session loop's.
  ModelStep: 'model.step',
  // Plan 102 slice 06a: an in-cluster session's pool key ran out mid-run and the
  // session moved onto the NEXT pool credential instead of failing. `output` is
  // `LlmFailoverOutputSchema` (`./payload`): `from_key`/`to_key`/`reason`/`reset_at`.
  // One row per hop; the spent key's own status move is its `billing.key.status_changed`.
  LlmFailover: 'llm.failover',
  ToolCallPending: 'tool.call.pending',
  ToolCallOk: 'tool.call.ok',
  ToolCallErr: 'tool.call.err',
  McpToolCallPending: 'mcp_tool.call.pending',
  McpToolCallOk: 'mcp_tool.call.ok',
  McpToolCallErr: 'mcp_tool.call.err',
  PolicyGateRefused: 'policy.gate.refused',
  EscalationFired: 'escalation.fired',
  // Plan 101 N3: an escalation whose condition — PR/issue, category, channel class,
  // and the head its links sign — was already told inside the refile window. The row
  // is written (audit is trust) but fired NOTHING, so it is not an
  // `escalation.fired`: a count of those is a count of tellings. `output.repeatOf`
  // names the first telling.
  EscalationRepeated: 'escalation.repeated',
  HandoffFired: 'handoff.fired',
  // NO WRITER from the escalation dispatcher since #5689 — its routed mail leg is
  // deleted and it sends nothing itself. The kind is kept so old rows still render;
  // the other writers (Stripe events, the key lanes, the wiring's own mail) remain.
  EmailSent: 'email.sent',
  // A mail was SKIPPED because nobody could be addressed. The one writer now:
  // - owner mail (`ownerMailTargets`, `packages/agent/src/owner-mail.ts`, deep sweep
  //   03-1), when `resolveAccountMailRecipients` found nobody to mail, with input
  //   `{ reason, ownerAdminCount, purpose, template }`. Its callers: the worker's
  //   owner notices, the `email.lifecycle` lane, agents-manager's spend and credit
  //   notices. One row per skipped notice.
  // The escalation email leg wrote it too, when the account had no `email_routes` row
  // (neither `escalate.<category>` nor `default`), with the `category` in the input.
  // NO WRITER there since #5689: that routed leg is deleted (an escalation now fans out
  // instead of mailing a routed copy). Its old rows still render, so the kind is kept.
  // The counterpart of `email.sent`, and a FIRST-CLASS kind for the same reason the
  // billing rows are: "has the email leg ever worked, and for whom" must be one
  // `WHERE kind IN ('email.sent','email.route_missing')` query off the indexed
  // column, not a text search over `tool.call.err` bodies.
  //
  // It exists because the absence was unobservable. Measured 2026-08-29: 1,347
  // `escalations` rows since 2026-07-22, `delivered_at` NULL on every one, ZERO
  // `email.sent` rows all-time, and `email_routes` empty — the leg had never
  // delivered once and nothing in the schema said so. "Never configured" and
  // "delivered" were indistinguishable in the data.
  //
  // Maintainer-visible: a mail that reached nobody is exactly the fact a
  // maintainer needs, and the remedy is theirs to apply.
  EmailRouteMissing: 'email.route_missing',
  // A mail to ONE recipient failed at the transport (CodeRabbit on #5815): the
  // durable record of a send that did not leave, so "who was not told" survives
  // the pod log. Not `notification.suppressed`, which is a policy skip, nor
  // `email.route_missing`, which means nobody could be addressed. Input:
  // `{ template, purpose?, errorClass, reason, address }` (the store redacts the
  // address). Every writer:
  // - the weekly digest (`apps/worker/src/cron/digest.ts`), per recipient whose send
  //   threw;
  // - owner mail (`sendOwnerMail`, `packages/agent/src/owner-mail.ts`), per address
  //   whose send threw, before the call's AggregateError.
  // Maintainer-visible: a member who was not told is theirs to chase.
  EmailFailed: 'email.failed',
  // Plan 101 N1: an address was suppressed, or mail to a suppressed one was withheld.
  // Every writer, and how often each writes:
  // - `reason: 'hard_bounce'`: the fanout (`apps/worker/src/notifications/wiring.ts`)
  //   and the `email.lifecycle` lane (`lifecycle-email.ts`), once per permanent bounce;
  // - `reason: 'address_suppressed'` from the fanout: once per (account, address) per
  //   30 days (`SUPPRESSION_SKIP_AUDIT_WINDOW_MS`, deep sweep 03-11), because the
  //   fanout mails per event and one dead address wrote 1,944 rows before;
  // - `reason: 'address_suppressed'` from owner mail (`ownerMailTargets`,
  //   `packages/agent/src/owner-mail.ts`): EVERY skip. Owner mail is a few notices per
  //   account (a key event, a billing pause, a weekly digest), so per skip is cheap.
  // Tenant-anchored and maintainer-visible: the recipient is one of their members,
  // and the remedy (fix the address, then lift it) is theirs.
  NotificationSuppressed: 'notification.suppressed',
  // #6358: a WAITING email (a `deferred` `notifications` row) left for a non-delivery
  // status without being sent. `input.because` says why: `turned_off` (the recipient or
  // the account switched it off), `no_address` (nothing could receive it), `answered` (its
  // ask was answered or re-armed while it waited). It exists because `turned_off` and
  // `answered` store the SAME row status, `suppressed`. An escalation the rows carried
  // and moved tells them apart (`opted_out` vs `collapsed`); for rows whose escalation
  // did not move, or that carried none, this row is the only record of which applied.
  // Not `notification.suppressed`, which is about an ADDRESS, nor
  // `email.failed`, a send that was tried. The one writer: `cron.email_bundle`
  // (`apps/worker/src/cron/email-bundle.lane.ts`), once per `refuseDeferredEmail` call
  // that moved a row, in that call's transaction. Maintainer-visible: whether a member
  // was told is theirs to know.
  EmailDeferredRefused: 'email.deferred_refused',
  // #3956: a push subscription (one member's browser/device) was DELETED. `input.reason`
  // says why: `gone` (the push service answered 404/410, with its `status`) or
  // `forbidden` (the send-time SSRF gate refused the endpoint, with its `refusal`).
  // `input` carries the endpoint's `origin` and NEVER the endpoint itself: the full URL
  // is a bearer capability. `output.removed` is false when the row was already gone.
  // The one writer: the `notify.fanout` push channel's `removeTarget`
  // (`prunePushTarget`, `apps/worker/src/notifications/wiring.ts`), once per prune, on
  // the notification's account. Maintainer-visible: a member no longer reached by push
  // on that device is theirs to know.
  PushTargetPruned: 'push.target_pruned',
  // #213: BYOK provider out of credits → the key is flipped `invalid`, which
  // stops dispatch on the account. The pause is a state change, so it gets its
  // own audit row independent of the (best-effort) maintainer email.
  AccountPaused: 'account.paused',
  // #230: a paused account resumes — a Stripe `invoice.paid` cleared a billing
  // pause. The unpause is the state change that re-enables dispatch, so it gets
  // its own audit row, mirroring AccountPaused.
  AccountResumed: 'account.resumed',
  // #155: a billing-ledger write to Stripe — today the runner-minute lanes
  // (`runner-minutes-report.lane.ts` per drain tick, `runner-minutes-reconcile.lane.ts`
  // per reconcile tick). A metering write is a money-path state change, so it gets
  // its own audit row — same rationale as AccountPaused. (The handled-unit usage
  // reporter that first wrote this kind was retired, #4743.)
  BillingMetered: 'billing.metered',
  // #1743: the BYOK-key + billing-preference LIFECYCLE, written by the api's
  // billing deps (`apps/api` `deps/billing-audit.ts`) through the
  // account-anchored sink (`buildAccountAudit`) from every transport that
  // shares it — the org REST pool (`/v1/billing/keys*`), the personal mirror
  // (`/v1/me/keys*` + `/v1/me/llm-pref`), and the MCP `billing` / `account`
  // verbs. A captured key IS the tenant's inference credential and a kind flip
  // moves it across a billing boundary (its sessions book $0 or their
  // per-token price, `isFlatRateKey`), so "audit is trust" bites here exactly as it does for the
  // `donation.*` / `grant.*` rows beside these: the eleven are FIRST-CLASS
  // kinds rather than `mcp_tool.call.ok` rows carrying a name in `tool`,
  // because "show me every kind change / every capture" must stay one
  // `WHERE kind = …` query off the INDEXED column whichever door wrote it.
  // `tool` is null on all eleven — `appendEvent` refuses a tool name on a
  // non-tool-call kind. **Never in a payload**: the key material (the
  // `BillingAudit` type carries metadata only — row id, provider, the
  // previous/next kind, the cap, the cart).
  //   `billing.key.captured` / `.revoked` / `.verified` / `.invalid` /
  //     `.transport_failed` / `.rotated` — the credential lifecycle (#1515
  //     keeps a transport failure distinct from a provider rejection; #971 a
  //     refused rotate audits `.invalid`, the attempt being the fact).
  //   `billing.key.kind_changed` — the key's billing nature flipped (#1247);
  //     `input` carries BOTH `previous_kind` (null for a legacy row never
  //     classified) and `next_kind`, so the full transition is on the row.
  //   `billing.spend_cap.set` — the account's monthly cap moved.
  //   `billing.checkout.started` — a seat purchase was STARTED (intent, not
  //     money; the grant is only provable from the Stripe webhook's own trail).
  //   `billing.llm_pref.set` / `.cleared` — a member's model default moved.
  BillingKeyCaptured: 'billing.key.captured',
  BillingKeyRevoked: 'billing.key.revoked',
  BillingKeyVerified: 'billing.key.verified',
  BillingKeyInvalid: 'billing.key.invalid',
  BillingKeyTransportFailed: 'billing.key.transport_failed',
  // A key's MODEL parked while the credential stays `valid` — its `byok_key_models`
  // row taken out of resolution. TWO writers, one fact about one row:
  //   - #2844, the verify-time CORRECTNESS park: the credential verified and the model
  //     could not answer (`active` false; no `output.until`).
  //   - 2026-10-17, the dated CAPACITY park: a quota refusal (`rate_limited` /
  //     `credits_exhausted`) naming ONE model of a key that keeps another live one
  //     parks it until the stated reset — `output.until` + `output.reason`, the door
  //     on `input.door` (`KeyModelParkDoor`).
  // Not `invalid` in either: the credential was never the problem.
  BillingKeyModelParked: 'billing.key.model_parked',
  BillingKeyRotated: 'billing.key.rotated',
  BillingKeyKindChanged: 'billing.key.kind_changed',
  // #4240: a key-model's DETECTED limits moved — window, output ceiling or their
  // source — from the provider's `/models` answer (verify, the reverify sweep) or
  // its own overflow refusal (a fleet run). `input` carries the model with BOTH
  // `previous` and `next`, so the transition is on the row; a no-op refresh
  // writes nothing.
  BillingKeyLimitsChanged: 'billing.key.limits_changed',
  // #7810: the CATALOGUE SYNC reconciled a key's `byok_key_models` against the
  // provider's own `/models` answer: models ADDED (with the inferred tier and listed
  // price), DELISTED (gone from the listing: `active = false` + `delisted_at`, never
  // deleted) or RELISTED (back on the listing, the sync's own delist undone). `input`
  // carries `{ accountId, keyId, provider, added, delisted, relisted }`; a sync that
  // moved nothing writes no row. Machine-made, like `limits_changed`.
  BillingKeyModelsSynced: 'billing.key.models_synced',
  // #7810: a person edited a key's model catalogue through a product door
  // (`PUT /v1/billing/keys/:id/models/:model`, MCP `billing key_model_set`): one model's
  // tier and/or active flag set, or the model added. `input` carries
  // `{ accountId, keyId, model, before, after }`, `before` null for an add. A no-op
  // writes no row.
  BillingKeyModelEdited: 'billing.key.model_edited',
  // Plan 102 slice 01: `byok_keys.status` MOVED (valid -> credits_exhausted,
  // rate_limited -> valid, ...). The column is overwritten in place, so this row is
  // the key's only status history. `output` is `BillingKeyStatusChangedOutputSchema`
  // (`./payload`) — `from`/`to`/`reason`/`retry_at` — written at the caller seam that
  // already audits the transition, never inside the query.
  BillingKeyStatusChanged: 'billing.key.status_changed',
  // Plan 102 slice 05b: `byok_keys.priority` (the pool's try-order) MOVED through a
  // product door — `PATCH /v1/billing/keys/:id/priority`, its `/v1/me` mirror, or the
  // MCP `key_priority_set` actions. `output` is `BillingKeyPriorityChangedOutputSchema`
  // (`./payload`): `key_id`/`from`/`to`. A no-op write (from === to) writes no row.
  BillingKeyPriorityChanged: 'billing.key.priority_changed',
  // Capacity-aware scheduling: `byok_keys.capacity` (the owner's DECLARED concurrent
  // model streams for the key) MOVED through a product door — `PATCH
  // /v1/billing/keys/:id/capacity`, its `/v1/me` mirror, or the MCP
  // `key_capacity_set` actions. `output` is `BillingKeyCapacityChangedOutputSchema`
  // (`./payload`): `key_id`/`from`/`to`, `null` = undeclared. No-op writes no row.
  BillingKeyCapacityChanged: 'billing.key.capacity_changed',
  // #5203: an ORG key's `byok_keys.label` (the handle `llm_routes.key_label` pins it
  // by) MOVED through `PATCH /v1/billing/keys/:id/label`. `output` is
  // `BillingKeyLabelChangedOutputSchema` (`./payload`): `key_id`/`from`/`to`, `null` =
  // unnamed. Each pin the rename carried writes its own `llm_route.set`. No-op writes no row.
  BillingKeyLabelChanged: 'billing.key.label_changed',
  // #5116: a `key_outages` row was OPENED by `cron.key_outage`. One row per
  // open, on the TENANT chain — the customer-facing surfaces (#5119, #5120,
  // #5121) read the open row, not this row, so this is the audit half of the
  // transition only. `input` carries `{ outage_id, accountId }`, `output` the
  // counted `held`.
  BillingKeyOutageOpened: 'billing.key_outage.opened',
  // #5116: a `key_outages` row was CLOSED `drained` — held work read empty past
  // `KEY_OUTAGE_DRAIN_GRACE_MS`. `input` carries `{ outage_id, accountId }`,
  // `output` `{ close_reason: 'drained' }`; the `picked` close is #5118's row.
  BillingKeyOutageClosed: 'billing.key_outage.closed',
  // #5118: an owner's pick (`POST /v1/key-outage/pick`, MCP `billing
  // outage_pick`) closed an open outage with `reason: 'picked'`. `input`
  // carries `{ outage_id, key_id, key_label }` — the row's OWN account
  // anchor (`accountAudit(accountId, …)`) is not duplicated onto `input` —
  // `output` `{ tasks, asks }`: the counts the pick's CAS predicates moved
  // (a row that left `pending` meanwhile is not counted). This row IS the
  // outage's close record; the rendered banner is the outage row's own
  // renderer (#5119, #5121).
  BillingKeyOutagePicked: 'billing.key_outage.picked',
  BillingSpendCapSet: 'billing.spend_cap.set',
  BillingCheckoutStarted: 'billing.checkout.started',
  BillingLlmPrefSet: 'billing.llm_pref.set',
  BillingLlmPrefCleared: 'billing.llm_pref.cleared',
  // arc 101 slice 01: a Stripe `customer.subscription.*` webhook synced the
  // account's paid `seats` + `tier`. A seat decrease below the live AI-dev count
  // drains the excess boxes (non-destructive — never a kill), so the sync is a
  // billing + fleet state change and earns its own row (mirrors AccountPaused).
  AccountSeatsSynced: 'account.seats_synced',
  // #7662: a SHADOWED seat subscription — a second live seat subscription on the
  // account. The owning one keeps the seats while live (earliest `first_live_at`
  // picks a new owner only once it is not); this one sets nothing and the customer pays for both (also an operator alert). Written once, by
  // the billing transition that first saw it shadowed. `input` carries `{ reason:
  // 'shadowed_seat_subscription', accountId, subscriptionId, ownerSubscriptionId,
  // eventType }`.
  AccountSubscriptionUnowned: 'account.subscription_unowned',
  // #7662: the platform's runner-minute subscription the runner-minutes cron attached
  // (`accounts.stripe_runner_private_subscription_id`) ENDED, and the column was
  // cleared in the same transaction so a later one can attach. Billing state, so it
  // is audited; written by the billing transition driver. `input` carries
  // `{ reason: 'subscription_ended', accountId, subscriptionId, eventType }`.
  AccountRunnerSubscriptionDetached: 'account.runner_subscription_detached',
  // #7662: an `invoice.payment_failed` on a DONOR subscription, which never pauses the
  // account (a donor's dunning ends in `deleted`, which parks its gifts); it marks the
  // donor unpaid, which refuses new resource gifts. `input` carries `{ accountId,
  // subscriptionId, eventType, invoiceId }`.
  BillingInvoiceUnowned: 'billing.invoice_unowned',
} as const;
